import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useForm, useFieldArray } from 'react-hook-form';
import { Helmet } from 'react-helmet-async';
import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import dayjs from 'dayjs';
import { PageHeader } from '../../components/layout';
import { Card, CardBody, Button, Input, Select, Textarea, PageLoader } from '../../components/ui';
import { invoiceService, clientService, projectService } from '../../services';

export default function InvoiceForm() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);
  const [loading, setLoading] = useState(isEdit);
  const [submitting, setSubmitting] = useState(false);
  const [clients, setClients] = useState([]);
  const [projects, setProjects] = useState([]);
  
  const { register, handleSubmit, control, watch, reset, formState: { errors } } = useForm({
    defaultValues: {
      items: [{ description: '', quantity: 1, unitPrice: 0 }]
    }
  });
  
  const { fields, append, remove } = useFieldArray({ control, name: 'items' });
  const watchItems = watch('items');
  const watchClient = watch('client');
  
  useEffect(() => {
    fetchClients();
    if (isEdit) {
      fetchInvoice();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);
  
  useEffect(() => {
    if (watchClient) {
      fetchClientProjects(watchClient);
    }
  }, [watchClient]);
  
  const fetchClients = async () => {
    try {
      const response = await clientService.getClients({ limit: 100 });
      setClients(response.data);
    } catch (error) {
      console.error('Failed to fetch clients:', error);
    }
  };
  
  const fetchClientProjects = async (clientId) => {
    try {
      const response = await projectService.getProjects({ client: clientId, limit: 100 });
      setProjects(response.data);
    } catch (error) {
      console.error('Failed to fetch projects:', error);
    }
  };
  
  const fetchInvoice = async () => {
    try {
      const invoice = await invoiceService.getInvoice(id);
      reset({
        client: invoice.client?._id || '',
        project: invoice.project?._id || '',
        dueDate: dayjs(invoice.dueDate).format('YYYY-MM-DD'),
        items: invoice.items.map(item => ({
          description: item.description,
          quantity: item.quantity,
          unitPrice: item.unitPrice
        })),
        notes: invoice.notes || '',
        terms: invoice.terms || ''
      });
      if (invoice.client?._id) {
        fetchClientProjects(invoice.client._id);
      }
    } catch (error) {
      toast.error(t('errors.notFound'));
      navigate('/invoices');
    } finally {
      setLoading(false);
    }
  };
  
  const calculateTotal = () => {
    if (!watchItems) return 0;
    return watchItems.reduce((sum, item) => {
      return sum + (parseFloat(item.quantity) || 0) * (parseFloat(item.unitPrice) || 0);
    }, 0);
  };
  
  const onSubmit = async (data) => {
    setSubmitting(true);
    try {
      const invoiceData = {
        client: data.client,
        project: data.project || null,
        dueDate: data.dueDate,
        items: data.items.map(item => ({
          description: item.description,
          quantity: parseFloat(item.quantity),
          unitPrice: parseFloat(item.unitPrice),
          total: parseFloat(item.quantity) * parseFloat(item.unitPrice)
        })),
        notes: data.notes || null,
        terms: data.terms || null
      };
      
      if (isEdit) {
        await invoiceService.updateInvoice(id, invoiceData);
        toast.success(t('success.updated'));
      } else {
        const invoice = await invoiceService.createInvoice(invoiceData);
        toast.success(t('success.created'));
        navigate(`/invoices/${invoice._id}`);
        return;
      }
      navigate('/invoices');
    } catch (error) {
      toast.error(error.response?.data?.message || t('errors.serverError'));
    } finally {
      setSubmitting(false);
    }
  };
  
  const clientOptions = [
    { value: '', label: 'Select client' },
    ...clients.map(c => ({ value: c._id, label: c.name }))
  ];
  
  const projectOptions = [
    { value: '', label: 'Select project (optional)' },
    ...projects.map(p => ({ value: p._id, label: p.name }))
  ];
  
  if (loading) {
    return <PageLoader />;
  }
  
  return (
    <>
      <Helmet>
        <title>{isEdit ? t('invoices.editInvoice') : t('invoices.newInvoice')} - ProCTM</title>
      </Helmet>
      
      <PageHeader
        title={isEdit ? t('invoices.editInvoice') : t('invoices.newInvoice')}
        backUrl="/invoices"
      />
      
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <Card>
          <CardBody>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Select
                label={t('projects.client')}
                options={clientOptions}
                required
                error={errors.client?.message}
                {...register('client', { required: t('errors.required') })}
              />
              
              <Select
                label="Project"
                options={projectOptions}
                {...register('project')}
              />
              
              <Input
                label={t('invoices.dueDate')}
                type="date"
                required
                error={errors.dueDate?.message}
                {...register('dueDate', { required: t('errors.required') })}
              />
            </div>
          </CardBody>
        </Card>
        
        <Card>
          <CardBody>
            <h3 className="text-lg font-medium mb-4">{t('invoices.items')}</h3>
            
            <div className="space-y-4">
              {fields.map((field, index) => (
                <div key={field.id} className="flex gap-4 items-start">
                  <div className="flex-1">
                    <Input
                      label={index === 0 ? 'Description' : undefined}
                      placeholder="Item description"
                      error={errors.items?.[index]?.description?.message}
                      {...register(`items.${index}.description`, { required: t('errors.required') })}
                    />
                  </div>
                  <div className="w-24">
                    <Input
                      label={index === 0 ? 'Qty' : undefined}
                      type="number"
                      min="1"
                      {...register(`items.${index}.quantity`, { required: true, min: 1 })}
                    />
                  </div>
                  <div className="w-32">
                    <Input
                      label={index === 0 ? 'Unit Price' : undefined}
                      type="number"
                      step="0.01"
                      min="0"
                      {...register(`items.${index}.unitPrice`, { required: true, min: 0 })}
                    />
                  </div>
                  <div className="w-32">
                    <div className={index === 0 ? 'pt-7' : ''}>
                      <p className="text-sm font-medium">
                        ${((watchItems?.[index]?.quantity || 0) * (watchItems?.[index]?.unitPrice || 0)).toFixed(2)}
                      </p>
                    </div>
                  </div>
                  <div className={index === 0 ? 'pt-7' : ''}>
                    {fields.length > 1 && (
                      <Button
                        type="button"
                        variant="ghost"
                        onClick={() => remove(index)}
                      >
                        <TrashIcon className="w-4 h-4 text-danger" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
            
            <Button
              type="button"
              variant="outline"
              size="sm"
              icon={PlusIcon}
              onClick={() => append({ description: '', quantity: 1, unitPrice: 0 })}
              className="mt-4"
            >
              Add Item
            </Button>
            
            <div className="mt-6 pt-6 border-t border-gray-200">
              <div className="flex justify-end">
                <div className="w-64 space-y-2">
                  <div className="flex justify-between text-lg font-semibold">
                    <span>{t('invoices.total')}:</span>
                    <span>${calculateTotal().toFixed(2)}</span>
                  </div>
                </div>
              </div>
            </div>
          </CardBody>
        </Card>
        
        <Card>
          <CardBody>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Textarea
                label={t('common.notes')}
                rows={4}
                placeholder="Notes to the client"
                {...register('notes')}
              />
              <Textarea
                label="Terms & Conditions"
                rows={4}
                placeholder="Payment terms"
                {...register('terms')}
              />
            </div>
          </CardBody>
        </Card>
        
        <div className="flex justify-end gap-3">
          <Button type="button" variant="outline" onClick={() => navigate('/invoices')}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" loading={submitting}>
            {isEdit ? t('common.update') : t('common.create')}
          </Button>
        </div>
      </form>
    </>
  );
}
